import { useState } from "react";
import { Brain, Cpu, Database, Clock, Copy, Check, Code, Globe } from "lucide-react";

const PROVIDER_COLORS = {
  openai: "#10B981",
  anthropic: "#F5DF4D",
  groq: "#3B82F6",
  openrouter: "#A78BFA",
};

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex justify-between items-center group">
      <span className="font-mono text-[10px] text-[#475569]">{label}</span>
      <button
        data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
        onClick={handleCopy}
        className="flex items-center gap-1 font-mono text-[10px] text-[#94A3B8] hover:text-[#E5FF00] transition-colors"
      >
        <span className="truncate max-w-[140px]">{value}</span>
        {copied ? <Check size={10} className="text-[#10B981]" /> : <Copy size={10} className="opacity-0 group-hover:opacity-100" />}
      </button>
    </div>
  );
}

export default function Inspector({ chat, messages, providers, onPublish, user }) {
  const [showSDK, setShowSDK] = useState(false);

  if (!chat) {
    return (
      <div data-testid="inspector-empty" className="w-[300px] min-w-[300px] h-screen bg-[#080B10] border-l border-[#273142] flex flex-col items-center justify-center">
        <div className="text-center px-6">
          <Cpu size={24} className="text-[#273142] mx-auto mb-3" />
          <p className="text-[#475569] text-xs font-mono uppercase tracking-widest">No Active Session</p>
        </div>
      </div>
    );
  }

  const provider = providers.find((p) => p.id === chat.provider_id);
  const color = PROVIDER_COLORS[chat.provider_type] || "#94A3B8";
  const msgCount = messages.length;
  const userMsgs = messages.filter((m) => m.role === "user").length;
  const aiMsgs = messages.filter((m) => m.role === "assistant").length;
  const memoryMsgs = messages.filter((m) => m.memory_context).length;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://chat-persistence-3.preview.emergentagent.com";

  const sdkSnippet = `from anymind import Agent

agent = Agent(
    chat_id="${chat.chat_id || chat.id}",
    backend_url="${backendUrl}"
)

response = agent.chat("Your message")
print(response)`;

  return (
    <div data-testid="inspector-panel" className="w-[300px] min-w-[300px] h-screen bg-[#080B10] border-l border-[#273142] flex flex-col overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-[#273142]">
        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569] mb-2">Session Inspector</p>
        <h3 className="text-sm font-semibold tracking-tight truncate">{chat.title}</h3>
      </div>

      {/* IDs */}
      <div className="p-4 border-b border-[#273142]">
        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569] mb-3">Identifiers</p>
        <div className="space-y-2">
          <CopyField label="CHAT_ID" value={chat.chat_id || chat.id} />
          <CopyField label="LLM_ID" value={chat.llm_id || chat.provider_id} />
        </div>
      </div>

      {/* Provider Info */}
      <div className="p-4 border-b border-[#273142]">
        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569] mb-3">Provider</p>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2" style={{ backgroundColor: color }} />
          <span className="text-sm font-medium">{provider?.display_name || "Unknown"}</span>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="font-mono text-[10px] text-[#475569]">TYPE</span>
            <span className="font-mono text-[10px] text-[#94A3B8] uppercase">{chat.provider_type}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-mono text-[10px] text-[#475569]">MODEL</span>
            <span className="font-mono text-[10px] text-[#94A3B8]">{chat.model_name}</span>
          </div>
          {provider && (
            <div className="flex justify-between">
              <span className="font-mono text-[10px] text-[#475569]">KEY</span>
              <span className="font-mono text-[10px] text-[#94A3B8]">{provider.api_key_masked}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="p-4 border-b border-[#273142]">
        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569] mb-3">Statistics</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#111620] border border-[#273142] p-3">
            <Database size={14} className="text-[#475569] mb-1" />
            <p className="text-lg font-semibold">{msgCount}</p>
            <p className="font-mono text-[10px] text-[#475569] uppercase">Messages</p>
          </div>
          <div className="bg-[#111620] border border-[#273142] p-3">
            <Brain size={14} className="text-[#E5FF00] mb-1" />
            <p className="text-lg font-semibold">{memoryMsgs}</p>
            <p className="font-mono text-[10px] text-[#475569] uppercase">w/ Memory</p>
          </div>
          <div className="bg-[#111620] border border-[#273142] p-3">
            <span className="text-xs text-[#475569]">U</span>
            <p className="text-lg font-semibold">{userMsgs}</p>
            <p className="font-mono text-[10px] text-[#475569] uppercase">User</p>
          </div>
          <div className="bg-[#111620] border border-[#273142] p-3">
            <span className="text-xs text-[#E5FF00]">AI</span>
            <p className="text-lg font-semibold">{aiMsgs}</p>
            <p className="font-mono text-[10px] text-[#475569] uppercase">Assistant</p>
          </div>
        </div>
      </div>

      {/* SDK Snippet */}
      <div className="p-4 border-b border-[#273142]">
        <button
          data-testid="toggle-sdk-snippet"
          onClick={() => setShowSDK(!showSDK)}
          className="w-full flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569] hover:text-[#E5FF00] transition-colors mb-2"
        >
          <Code size={12} />
          <span>SDK Usage</span>
          <span className="ml-auto text-[10px]">{showSDK ? "[-]" : "[+]"}</span>
        </button>
        {showSDK && (
          <div className="bg-[#080B10] border border-[#273142] p-3 animate-fade-in">
            <pre className="font-mono text-[10px] text-[#94A3B8] whitespace-pre-wrap leading-relaxed overflow-x-auto">
              {sdkSnippet}
            </pre>
            <button
              data-testid="copy-sdk-snippet"
              onClick={() => navigator.clipboard.writeText(sdkSnippet)}
              className="mt-2 flex items-center gap-1 font-mono text-[10px] text-[#475569] hover:text-[#E5FF00] transition-colors"
            >
              <Copy size={10} />
              COPY
            </button>
          </div>
        )}
      </div>

      {/* Memory Status */}
      <div className="p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569] mb-3">Memory Layer</p>
        <div className="flex items-center gap-2 p-3 bg-[#E5FF00]/5 border border-[#E5FF00]/20">
          <Brain size={14} className="text-[#E5FF00]" />
          <div>
            <p className="text-xs font-medium text-[#E5FF00]">Memory Connected</p>
            <p className="font-mono text-[10px] text-[#475569]">Namespace: chat_{(chat.chat_id || chat.id).slice(0, 8)}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-[#475569]">
          <Clock size={12} />
          <span className="font-mono text-[10px]">
            Created: {new Date(chat.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Publish to Marketplace */}
        {onPublish && user && (
          <button
            data-testid="publish-capsule-button"
            onClick={onPublish}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-transparent text-[#E5FF00] border border-[#E5FF00]/40 hover:bg-[#E5FF00]/10 font-mono text-[10px] uppercase tracking-wider px-3 py-2 transition-colors rounded-lg"
          >
            <Globe size={12} />
            Publish to Marketplace
          </button>
        )}
      </div>
    </div>
  );
}
